import styled from "@emotion/styled";
import { ContextButton, red, StepInfoBox } from "@stepfinance/react";
import type { TransactionCountByMonth } from "@stepfinance/transaction-history";
import { Col, Row } from "antd";
import type { FC } from "react";
import shallow from "zustand/shallow";

import { ReactComponent as LockIcon } from "../../assets/lock.svg";
import { useLanguageContext } from "../../contexts/language/language";
import { useTransactionHistoryStore } from "../../hooks/stores/transactionHistoryStore";
import { formatStringWithPlaceholders } from "../../utils/utils";
import { TextDescription, TextTitle } from "../shared";
import { StepSkeleton } from "../StepSkeleton/StepSkeleton";

type MonthCardsProps = {
  monthStats?: TransactionCountByMonth;
  isLoading: boolean;
  isSuccess: boolean;
  userTierMaxTransactions: number;
};

export const MonthCards: FC<MonthCardsProps> = ({ userTierMaxTransactions, isLoading, monthStats, isSuccess }) => {
  let accumTransactions = 0;

  const [selectedYear, setTiersModalOpen, updatefilters] = useTransactionHistoryStore(
    (state) => [state.selectedYear, state.setTiersModalOpen, state.updateFilters],
    shallow,
  );

  const {
    currentLanguageData: {
      pages: { transactionHistory },
    },
    // languageSetting,
  } = useLanguageContext();

  const onSeeMoreClick = (month: Date) => {
    const startDate = new Date(month.getUTCFullYear(), month.getUTCMonth(), 1, 0, 0, 0, 0).toISOString();
    const endDate = new Date(month.getUTCFullYear(), month.getUTCMonth() + 1, 0, 23, 59, 59, 999).toISOString();

    updatefilters({ startDate, endDate });
    document.querySelector("#transactions-table")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <div className="transactions-container">
      <div className="title-container">
        <TextTitle>
          {formatStringWithPlaceholders(transactionHistory.calendar.byYearTransactions, selectedYear)}
        </TextTitle>
      </div>
      <div className="monthly-transactions-container">
        <Row gutter={[16, 20]}>
          {isLoading &&
            [1, 2, 3, 4, 5, 6, 7, 8].map((_, i) => (
              <Col key={i} xs={24} sm={12} lg={8} xl={6}>
                <MonthInformationSkeleton />
              </Col>
            ))}
          {isSuccess &&
            monthStats &&
            monthStats.map((month, i) => {
              const beginningMonthTxCount = accumTransactions;
              accumTransactions += month.fee_payer_count;
              // Full Tier unlocks all txs
              const tierTransactions =
                userTierMaxTransactions === -1 ? Number.POSITIVE_INFINITY : userTierMaxTransactions;

              const includesLockedTransactions = accumTransactions > tierTransactions;
              const partiallyUnlockedMonth = beginningMonthTxCount < tierTransactions && includesLockedTransactions;
              let unlockedTxCount = 0;
              if (partiallyUnlockedMonth) {
                unlockedTxCount = tierTransactions - beginningMonthTxCount;
              } else if (!includesLockedTransactions) {
                unlockedTxCount = month.fee_payer_count;
              }

              const lockedTxCount = month.fee_payer_count - unlockedTxCount;

              const monthDate = new Date(month.month);
              const now = Date.now();
              const isFutureMonth = monthDate.getTime() > now;
              return (
                <Col key={i} xs={24} sm={12} lg={8} xl={6}>
                  <StepInfoBox presentational disabled={isFutureMonth}>
                    <div className="monthly-transaction-box">
                      <StepInfoBox.Title>
                        <TextTitle size="m" disabled={isFutureMonth}>
                          {monthDate.toLocaleString("default", { month: "long", timeZone: "UTC" })}
                        </TextTitle>
                      </StepInfoBox.Title>
                      <StepInfoBox.Description>
                        <div className="description-container">
                          {isFutureMonth ? (
                            <TextDescription>{transactionHistory.calendar.comingMonth}</TextDescription>
                          ) : month.fee_payer_count > 0 ? (
                            <TextDescription>
                              <div className="transactions-label">
                                {unlockedTxCount > 0 &&
                                  formatStringWithPlaceholders(
                                    transactionHistory.calendar.transactionsAmount,
                                    unlockedTxCount,
                                  )}
                                {unlockedTxCount > 0 && lockedTxCount > 0 && " + "}
                                {lockedTxCount > 0 && (
                                  <LockedTransactionsLabel>
                                    <LockIcon className="locked-transactions-icon" />
                                    {unlockedTxCount > 0 && lockedTxCount > 0
                                      ? lockedTxCount
                                      : formatStringWithPlaceholders(
                                          transactionHistory.calendar.transactionsLocked,
                                          lockedTxCount,
                                        )}
                                  </LockedTransactionsLabel>
                                )}
                              </div>
                            </TextDescription>
                          ) : (
                            <TextDescription>{transactionHistory.calendar.noTransactions}</TextDescription>
                          )}
                        </div>
                        {!isFutureMonth &&
                          (unlockedTxCount > 0 ? (
                            <ContextButton
                              disabled={month.fee_payer_count <= 0}
                              className="see-more-button"
                              onClick={() => onSeeMoreClick(monthDate)}
                            >
                              {transactionHistory.calendar.seeMore}
                            </ContextButton>
                          ) : (
                            <ContextButton
                              onClick={() => setTiersModalOpen(true)}
                              className="see-more-button"
                              disabled={month.fee_payer_count <= 0}
                            >
                              {transactionHistory.calendar.unlockButton}
                            </ContextButton>
                          ))}
                      </StepInfoBox.Description>
                    </div>
                  </StepInfoBox>
                </Col>
              );
            })}
        </Row>
      </div>
    </div>
  );
};

const MonthInformationSkeleton: React.FC = () => {
  return (
    <StepInfoBox presentational>
      <div className="monthly-transaction-box">
        <StepInfoBox.Title>
          <StepSkeleton size="m" width={140} />
        </StepInfoBox.Title>
        <StepInfoBox.Description>
          <div className="description-container">
            <StepSkeleton size="s" width={150} style={{ marginTop: 4 }} />
          </div>
        </StepInfoBox.Description>
        <StepSkeleton size="l" width={110} style={{ marginTop: 12 }} />
      </div>
    </StepInfoBox>
  );
};

const LockedTransactionsLabel = styled.div`
  color: ${red};
  display: flex;
  align-items: center;
  .locked-transactions-icon {
    margin: 0px 4px;
    height: 14px;
    width: 14px;
  }
`;
